import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

export default function CookiePolicy() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="border-b border-stone-200 bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl tracking-tight inline-flex items-center gap-2" data-testid="brand-logo">
            <FoxLogo size={32} />Xenia<span className="text-blue-700">.</span>
          </Link>
          <LanguageSelector />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="cookie-policy-content">
        <p className="text-xs tracked text-blue-700 mb-3">REG. UE 2016/679 · DIRETTIVA EPRIVACY 2002/58/CE</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{t("privacy.cookie_title")}</h1>
        <p className="text-stone-600 mb-2">
          Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
        </p>
        <p className="text-xs text-stone-500 italic mb-12 p-3 bg-amber-50 border border-amber-200">
          ⚠️ {t("privacy.legal_disclaimer")}
        </p>

        <Section title="Cosa sono i cookie">
          <p>I cookie sono piccoli file di testo che il sito web salva sul tuo dispositivo (computer, tablet, smartphone) durante la navigazione. Servono a far funzionare il sito o a ricordare le tue preferenze.</p>
        </Section>

        <Section title="Cookie utilizzati da Xenia">
          <p className="mb-4">Xenia utilizza <strong>esclusivamente cookie tecnici</strong>, necessari al funzionamento del Servizio, per i quali <strong>non è richiesto consenso preventivo</strong> ai sensi del Provvedimento del Garante 10/6/2021. Non utilizziamo cookie di profilazione, pubblicitari o di terze parti per finalità di marketing.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Nome</th>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Tipo</th>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Finalità</th>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Durata</th>
                </tr>
              </thead>
              <tbody className="text-stone-700">
                <CookieRow n="access_token" t="Tecnico" f="Mantenere la sessione utente autenticata (JWT, httpOnly)" d="7 giorni" />
                <CookieRow n="session_token" t="Tecnico" f="Sessione Google OAuth (httpOnly)" d="7 giorni" />
                <CookieRow n="xenia_property_id" t="Tecnico" f="Ricorda l'ultima struttura selezionata in caso multi-property" d="Sessione" />
                <CookieRow n="i18nextLng" t="Funzionale" f="Lingua preferita dell'interfaccia" d="365 giorni" />
                <CookieRow n="xenia_cookie_consent" t="Tecnico" f="Memorizza la presa visione di questa Cookie Policy" d="365 giorni" />
                <CookieRow n="xenia_onboarding_done" t="Funzionale" f="Indica se il wizard di benvenuto è stato completato" d="Permanente (localStorage)" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Cookie di terze parti">
          <p>Le pagine pubbliche (landing) caricano <strong>immagini ottimizzate da Unsplash</strong>. Tale servizio non installa cookie di profilazione, ma può registrare richieste HTTP standard (User-Agent, IP) ai fini della sola erogazione delle immagini.</p>
          <p className="mt-3">Durante il pagamento dell'abbonamento sei reindirizzato sul dominio sicuro di <strong>Stripe Checkout</strong>: i cookie utilizzati su <code>checkout.stripe.com</code> sono regolati dalla loro <a href="https://stripe.com/cookie-settings" target="_blank" rel="noreferrer" className="text-blue-700 underline">Cookie Policy</a>.</p>
        </Section>

        <Section title="Come gestire i cookie">
          <p>Puoi disattivare i cookie tramite le impostazioni del tuo browser. <strong>Attenzione</strong>: disabilitando i cookie tecnici, il login e la navigazione autenticata smetteranno di funzionare.</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li><a href="https://support.google.com/chrome/answer/95647" className="text-blue-700 underline" target="_blank" rel="noreferrer">Chrome</a></li>
            <li><a href="https://support.mozilla.org/kb/cookie" className="text-blue-700 underline" target="_blank" rel="noreferrer">Firefox</a></li>
            <li><a href="https://support.apple.com/safari" className="text-blue-700 underline" target="_blank" rel="noreferrer">Safari</a></li>
            <li><a href="https://support.microsoft.com/microsoft-edge" className="text-blue-700 underline" target="_blank" rel="noreferrer">Edge</a></li>
          </ul>
        </Section>

        <Section title="Contatti">
          <p>Per qualsiasi domanda relativa a questa Cookie Policy: <strong>privacy@xeniaregistry.com</strong>.</p>
          <p className="mt-3">Vai all'<Link to="/privacy" className="text-blue-700 underline">Informativa Privacy</Link> completa.</p>
        </Section>
      </main>

      <footer className="border-t border-stone-200 py-8 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} Xenia · GDPR-compliant
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-2xl tracking-tight mb-3">{title}</h2>
      <div className="text-stone-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function CookieRow({ n, t, f, d }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="p-3 align-top font-mono text-xs">{n}</td>
      <td className="p-3 align-top">{t}</td>
      <td className="p-3 align-top">{f}</td>
      <td className="p-3 align-top">{d}</td>
    </tr>
  );
}
